import React, { useEffect, useState } from 'react'
import { Typography, Grid, Container } from '@mui/material'
import Creator from '../creator'
import { useDispatch, useSelector } from 'react-redux'
import { playlistProgressSlice } from '../../../reducers'
import { DEFAULT_READ_TIME } from '../../../consts'
import useContentType from '../../../hooks/useContentType'
import { useSession } from '@emerald-works/react-auth'
import { useInView } from 'react-intersection-observer'
import { Breadcrumbs } from '../breadcrumbs'
import FloatingToolbar from '../floating-toolbar'
import { useStyles } from './style'
import Toolbar from '../toolbar'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { usePlaylistContext } from '../../playlist-progress/context'
import { ToggleViewedButton } from '../../action-buttons'
import RelatedArticles from '../related-articles'
import { useFormFactor } from '../../../hooks'

const Header = ({ content, playlistContextId, playlistBreadcrumbs, handleAddToPlaylist }) => {
  const { isAuthenticated } = useSession()
  const { isMobile, isSmallTablet, isLargeTablet, isDesktop, isLargeDesktop } = useFormFactor()
  const isTablet = isSmallTablet || isLargeTablet
  const [headerRef, headerInView] = useInView({ initialInView: true })
  const authorBy = () => <Creator name={author} author />
  const reviewerBy = () => <Creator name={reviewer} />
  const dispatch = useDispatch()
  const showLoadingIndicator = useSelector(playlistProgressSlice.selectors.selectLoadingIndicator)
  const [viewedButtonClicked, setViewedButtonClicked] = useState(false)
  const classes = useStyles()
  const {
    readTime = DEFAULT_READ_TIME, // default read time
    author = { name: 'the Mindtools Content Team' },
    reviewer,
    primaryType,
    title,
    subtitle,
    locationId
  } = content
  const { isMarkedAsRead } = usePlaylistContext()

  const { formatIcon, label } = useContentType({ primaryType })

  const [togglePlaylistContentRead] = useEvent([
    playlistProgressSlice.eventBus.togglePlaylistContentRead
  ])

  const handleViewed = () => {
    dispatch(playlistProgressSlice.actions.setLoadingIndicator({ visible: true }))
    setViewedButtonClicked(true)
    togglePlaylistContentRead.trigger({
      playlistId: playlistContextId,
      contentId: locationId,
      isRead: true
    })
  }

  useEffect(() => {
    setViewedButtonClicked(showLoadingIndicator && viewedButtonClicked)
  }, [showLoadingIndicator, viewedButtonClicked])

  return (
    <>
      <Breadcrumbs className={classes.breadcrumbs} content={playlistContextId ? playlistBreadcrumbs : content} />
      <Container>
        <Grid container className={classes.contentGrid}>
          <Grid className={classes.relatedArticlesGrid} item>
            {(isDesktop || isLargeDesktop) && !playlistContextId && <RelatedArticles title='Related Articles' />}
          </Grid>
          <Grid item>
            {(isDesktop || isLargeDesktop) && !headerInView && (
              <Toolbar variant='vertical' className={classes.verticalToolbar} handleAddToPlaylist={handleAddToPlaylist} />
            )}
            {(isMobile || isTablet) && (
              <FloatingToolbar
                open={!headerInView}
                title={title}
                toolbarEl={<Toolbar variant='floating' />}
              />
            )}
            <Typography
              variant='body1'
              className={classes.contentMetadata}
              data-test='article-read-time'
            >
              {formatIcon} {label} &#8226; {readTime} min read
            </Typography>
            <Typography
              variant='h3'
              component='h1'
              className={classes.title}
              data-test='article-title'
            >
              {title}
            </Typography>
            <Typography
              variant='h5'
              component='h2'
              color='textSecondary'
              className={classes.subtitle}
              data-test='article-subtitle'
            >
              {subtitle?.html}
            </Typography>
            <Grid
              container
              ref={headerRef}
              alignItems='center'
              spacing={3}
              className={classes.header}
              data-test='article-header'
            >
              <Grid item xs={12} sm='auto' className={classes.creatorsContainer}>
                <Grid container spacing={2} className={classes.creatorsGrid}>
                  {authorBy()}
                  {reviewer && reviewerBy()}
                </Grid>
              </Grid>
              {headerInView && isAuthenticated && (
                <Grid item xs={12} sm='auto' className={classes.toolbar}>
                  <Toolbar variant='inline' handleAddToPlaylist={handleAddToPlaylist} />
                </Grid>
              )}
              {playlistContextId && (
                <Grid item xs={12} sm='auto'>
                  <ToggleViewedButton
                    key='view'
                    viewed={isMarkedAsRead(playlistContextId, locationId)}
                    onClick={() => {
                      handleViewed(content)
                    }}
                    classes={{ root: classes.markAsViewedButton }}
                    loadingIndicator={viewedButtonClicked}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid item />
        </Grid>
      </Container>
    </>
  )
}

export default Header
